"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { ElementWithWebkitFS, DocumentWithWebkitFS } from "@/types/dom";

/**
 * useFullscreenAntiCheat — the shared fullscreen + anti-cheat cluster.
 *
 * Extracted (F9.7) from the two divergent copies `use-exam-security.ts` (real
 * exam) and `use-demo-exam-security.ts` (demo exam). Every listener, ref,
 * timing constant and penalty step is VERBATIM from `use-exam-security.ts`;
 * the deltas between the two originals are expressed as options:
 *
 * - `detectScreenCapture` — the real exam polls `display-capture` permission
 *   every 5s, the demo never did.
 * - `onPenalty` — the real hook applied the count penalty to the PAGE's
 *   `setTabSwitchCount` (seeded from cache/backend, so the core must NOT own
 *   that number); the demo owned its own count. Wrappers own the count and
 *   increment it here, before the timer penalty (same order as the originals).
 * - `penaltySeconds` / `sectionIdx` / `durationMode` — both applied -10s; the
 *   real one targets `timers[durationMode === "per_section" ? sectionIdx : 0]`,
 *   the demo wraps its single `timer` number as a one-element array.
 *
 * NOTE: Fullscreen API + screen-capture permission cannot be exercised under
 * jsdom, so this hook has no unit test; verification is the manual smoke test.
 */
export function useFullscreenAntiCheat<V extends string>(opts: {
  /** Current view; the cluster only arms itself on `"exam"`. */
  view: V;
  /** Timers penalized on violation: `timers[durationMode === "per_section" ? sectionIdx : 0]`. */
  setTimers: React.Dispatch<React.SetStateAction<number[]>>;
  /** Called once per counted violation, BEFORE the timer penalty is applied. Must close over setState only (it is captured by the anti-cheat effect, whose deps stay `[view, sectionIdx, durationMode]`). */
  onPenalty?: () => void;
  /** Seconds removed from the penalized timer. Default 10. */
  penaltySeconds?: number;
  /** Poll the `display-capture` permission and warn when it is granted. Default false. */
  detectScreenCapture?: boolean;
  /** Timer index for `per_section` mode. Default 0. */
  sectionIdx?: number;
  /** Default "global" (penalize `timers[0]`). */
  durationMode?: "global" | "per_section";
  /** Skip penalties within this window after entering the exam (fullscreen prompt causes blur). Default 2000. */
  graceMs?: number;
  /** Minimum ms between two penalties. Default 500. */
  debounceMs?: number;
}) {
  const {
    view,
    setTimers,
    onPenalty,
    penaltySeconds = 10,
    detectScreenCapture = false,
    sectionIdx = 0,
    durationMode = "global",
    graceMs = 2000,
    debounceMs = 500,
  } = opts;

  // Anti-cheat state
  const [penaltyFlash, setPenaltyFlash] = useState(false);
  const [penaltyMessage, setPenaltyMessage] = useState("");
  const [tabWarning, setTabWarning] = useState(false);
  const [showFullscreenWarning, setShowFullscreenWarning] = useState(false);
  const [showScreenRecordWarning, setShowScreenRecordWarning] = useState(false);
  const [isWindowBlurred, setIsWindowBlurred] = useState(false);

  // Anti-cheat refs
  const lastPenaltyRef = useRef(0);
  // Track whether WE initiated the fullscreen change to avoid false triggers
  const fsChangeByUsRef = useRef(false);
  // Grace period after entering exam — skip penalties during fullscreen prompt
  const examEnteredAtRef = useRef(0);

  // ===== FULLSCREEN =====
  const requestFullscreen = useCallback(() => {
    fsChangeByUsRef.current = true;
    const el = document.documentElement;
    const req =
      el.requestFullscreen ?? (el as ElementWithWebkitFS).webkitRequestFullscreen;
    if (req)
      req
        .call(el)
        .catch(() => {})
        .finally(() => {
          setTimeout(() => {
            fsChangeByUsRef.current = false;
          }, 500);
        });
  }, []);
  const exitFullscreen = useCallback(() => {
    const fse =
      document.fullscreenElement ||
      (document as DocumentWithWebkitFS).webkitFullscreenElement;
    if (fse) {
      fsChangeByUsRef.current = true;
      const exit =
        document.exitFullscreen ??
        (document as DocumentWithWebkitFS).webkitExitFullscreen;
      if (exit)
        exit
          .call(document)
          .catch(() => {})
          .finally(() => {
            setTimeout(() => {
              fsChangeByUsRef.current = false;
            }, 500);
          });
    }
  }, []);

  useEffect(() => {
    if (view === "exam") {
      examEnteredAtRef.current = Date.now();
      requestFullscreen();
    } else if (view === "result") {
      // Delay exit fullscreen so confetti animation starts first
      setTimeout(exitFullscreen, 300);
    } else if (view === "overview") exitFullscreen();
  }, [view, requestFullscreen, exitFullscreen]);

  // Fullscreen change detection — only warn when USER exits, not when WE do
  useEffect(() => {
    if (view !== "exam") return;
    const handleFsChange = () => {
      if (fsChangeByUsRef.current) return;
      const isFs = !!(
        document.fullscreenElement ||
        (document as DocumentWithWebkitFS).webkitFullscreenElement
      );
      if (!isFs) setShowFullscreenWarning(true);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    document.addEventListener("webkitfullscreenchange", handleFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      document.removeEventListener("webkitfullscreenchange", handleFsChange);
    };
  }, [view]);

  // ===== ANTI-CHEAT =====
  useEffect(() => {
    if (view !== "exam") return;
    const block = (e: Event) => e.preventDefault();
    const applyPenalty = (msg?: string) => {
      // Grace period: skip penalties within 2s of entering exam (fullscreen prompt causes blur)
      if (Date.now() - examEnteredAtRef.current < graceMs) return;
      const now = Date.now();
      if (now - lastPenaltyRef.current < debounceMs) return;
      lastPenaltyRef.current = now;
      onPenalty?.();
      const tIdx = durationMode === "per_section" ? sectionIdx : 0;
      setTimers((prev) => {
        const next = [...prev];
        next[tIdx] = Math.max(0, next[tIdx]! - penaltySeconds);
        return next;
      });
      setPenaltyFlash(true);
      setPenaltyMessage(msg || `-${penaltySeconds} detik! Jangan pindah tab`);
      setTimeout(() => setPenaltyFlash(false), 2000);
      setTabWarning(true);
    };
    const handleVisibility = () => {
      if (document.hidden) {
        setIsWindowBlurred(true);
        applyPenalty();
      } else {
        setIsWindowBlurred(false);
      }
    };
    // Removed window blur/focus — they fire on every modal/button interaction causing blinks.
    // visibilitychange is the only reliable way to detect user leaving the tab.
    const handleBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey && e.shiftKey && (e.key === "3" || e.key === "4" || e.key === "5")) {
        e.preventDefault();
        e.stopPropagation();
        applyPenalty(`-${penaltySeconds} detik! Screenshot terdeteksi`);
        return;
      }
      if (e.key === "PrintScreen") {
        e.preventDefault();
        applyPenalty(`-${penaltySeconds} detik! Screenshot terdeteksi`);
        return;
      }
      if (e.metaKey && e.shiftKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        applyPenalty(`-${penaltySeconds} detik! Screenshot terdeteksi`);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        applyPenalty(`-${penaltySeconds} detik! Print terdeteksi`);
        return;
      }
      if (
        e.key === "F12" ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "i")
      ) {
        e.preventDefault();
        return;
      }
    };
    const style = document.createElement("style");
    style.textContent = `@media print { body { display: none !important; } }`;
    document.head.appendChild(style);

    let screenCheckInterval: ReturnType<typeof setInterval> | null = null;
    const checkScreenCapture = async () => {
      try {
        if (navigator.permissions) {
          // `display-capture` is a valid PermissionName in browsers but not in
          // the TS DOM lib, so query it through a minimally-typed surface.
          const status = await (
            navigator.permissions as Permissions & {
              query: (desc: { name: string }) => Promise<PermissionStatus>;
            }
          ).query({ name: "display-capture" });
          if (status.state === "granted") setShowScreenRecordWarning(true);
        }
      } catch {}
    };
    if (detectScreenCapture) {
      checkScreenCapture();
      screenCheckInterval = setInterval(checkScreenCapture, 5000);
    }

    document.addEventListener("copy", block);
    document.addEventListener("paste", block);
    document.addEventListener("cut", block);
    document.addEventListener("contextmenu", block);
    document.addEventListener("visibilitychange", handleVisibility);
    document.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      document.removeEventListener("copy", block);
      document.removeEventListener("paste", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("visibilitychange", handleVisibility);
      document.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (screenCheckInterval) clearInterval(screenCheckInterval);
      style.remove();
    };
    // onPenalty / setTimers / the timing options are stable setState-or-constant
    // values passed through from the callers; deps kept identical to the
    // original effects (`[view, sectionIdx, durationMode]`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, sectionIdx, durationMode]);

  return {
    requestFullscreen,
    exitFullscreen,
    penaltyFlash,
    penaltyMessage,
    tabWarning,
    setTabWarning,
    showFullscreenWarning,
    setShowFullscreenWarning,
    showScreenRecordWarning,
    setShowScreenRecordWarning,
    isWindowBlurred,
  };
}
